import { useEffect } from "react";
import {
  loadCommandPalette,
  loadInboxView,
  loadListView,
  loadListsOverview,
  loadTerminalPanel,
} from "./lazy-imports";

/**
 * Idle-time preload of the heavy lazy chunks the user is likely to open
 * shortly after sign-in. Falls back to a setTimeout when
 * `requestIdleCallback` is unavailable.
 */
export function useWarmPreload({
  user,
  loading,
}: {
  user: unknown;
  loading: boolean;
}) {
  useEffect(() => {
    if (loading || !user) return;
    const preloadChunk = (loader: () => Promise<unknown>) => {
      void loader().catch(() => {
        // Best effort: keep startup resilient even if prefetch fails.
      });
    };
    const preloadViews = () => {
      preloadChunk(loadCommandPalette);
      preloadChunk(loadInboxView);
      preloadChunk(loadListView);
      preloadChunk(loadListsOverview);
      preloadChunk(loadTerminalPanel);
    };
    const idleWindow = window as Window & {
      requestIdleCallback?: (
        callback: (deadline: {
          didTimeout: boolean;
          timeRemaining: () => number;
        }) => void,
        options?: { timeout: number },
      ) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (typeof idleWindow.requestIdleCallback === "function") {
      const idleId = idleWindow.requestIdleCallback(() => preloadViews(), {
        timeout: 1500,
      });
      return () => {
        if (typeof idleWindow.cancelIdleCallback === "function") {
          idleWindow.cancelIdleCallback(idleId);
        }
      };
    }
    const timer = window.setTimeout(preloadViews, 900);
    return () => window.clearTimeout(timer);
  }, [loading, user]);
}
